import themeBuilderExport from './customized.theme.json';

/**
 * The house style for every chart in the app.
 *
 * A theme is the only place ECharts lets you say "this is what our charts look like" once: it is
 * merged underneath every option, so a chart states what it *decides* and inherits what it merely
 * *looks like*. Anything that answers a question about the data — which series go on which axis,
 * what a tick means, where the plot starts — stays in that chart's own option.
 *
 * The design itself lives in `customized.theme.json`, straight from Apache's Theme Builder and
 * filtered to the features our ECharts bundle supports. The overrides below add things the builder
 * has no field for, each with the reason it is there. There is one copy of the design and this is not it.
 *
 * The builder ships its export as `customized.js` and tells you to load it with a `<script>` tag.
 * That cannot work here: the file is UMD and looks for `echarts` as a global, while this app
 * bundles it as a module. The object is passed to `provideEchartsCore` instead, so the directive
 * applies it whenever it initializes a chart.
 */
/**
 * The Theme Builder's export, as it comes. `customized.theme.json` is the object out of
 * `customized.js` with the UMD wrapper stripped. Updating the design means replacing that one file
 * and nothing else.
 */
const THEME_BUILDER_EXPORT: Record<string, unknown> = themeBuilderExport;

/**
 * The tokens, exported because markup built by hand needs the same values and must not carry a
 * second copy of them — the tooltip panel is drawn as HTML and inherits nothing from ECharts.
 */
export const CHART_SERIES_COLORS: string[] = THEME_BUILDER_EXPORT['color'] as string[];
export const CHART_AXIS_COLOR = '#87929B';
export const CHART_GRID_COLOR = '#424C57';
export const CHART_RESIDUAL_COLOR = '#BCC8D1';
export const CHART_SURFACE_COLOR = THEME_BUILDER_EXPORT['backgroundColor'] as string;
export const CHART_PRIMARY_COLOR = CHART_SERIES_COLORS[0];
export const CHART_TEXT_COLOR = '#FFFFFF';

/** Extend the house palette without changing colours when the same chart reloads. */
export function chartSeriesColor(index: number): string {
  if (index < CHART_SERIES_COLORS.length) {
    return CHART_SERIES_COLORS[index];
  }

  // Golden-angle spacing spreads subsequent colours across the hue wheel.
  const hue = ((index + 1) * 137.508) % 360;
  return `hsl(${hue.toFixed(2)}, 70%, 60%)`;
}

export const EM_CHART_THEME: Record<string, unknown> = {
  // Unsupported component defaults can leak through getOption() into PDF chart copies, where
  // ECharts treats them as requested components and reports missing imports.
  ...Object.fromEntries([
    'color', 'backgroundColor', 'textStyle', 'title', 'line', 'bar', 'pie',
    'categoryAxis', 'valueAxis', 'logAxis', 'timeAxis', 'legend', 'tooltip', 'grid',
  ].map(key => [key, THEME_BUILDER_EXPORT[key]])),

  tooltip: {
    ...(THEME_BUILDER_EXPORT['tooltip'] as Record<string, unknown>),
    // The builder has no field for any of this, because it does not know our tooltips draw their
    // own panel in the formatter — the border has to take the colour of whatever the pointer is on,
    // which no theme can express.
    backgroundColor: 'transparent',
    borderWidth: 0,
    extraCssText: 'box-shadow:none;padding:0;',
    padding: 0,
    // Not a cosmetic choice. With a transition set, ECharts throttles its position update to 50ms
    // (TooltipView `_updatePosition`) while it rewrites the panel's markup on every single mouse
    // move — so anything styled from that update is missing from most frames and flashes back in
    // twenty times a second. At zero the two run together again.
    transitionDuration: 0,
  },

  // The zoom control is house style throughout and the builder has no section for it.
  dataZoom: {
    backgroundColor: 'rgba(135, 146, 155, 0.22)',
    borderColor: 'transparent',
    borderRadius: 10,
    fillerColor: CHART_PRIMARY_COLOR,
    // Two subpaths: the rounded grip, and a bare vertical line inside it. The line has no area, so
    // it is only stroked — that is the 1px mark in the middle of the handle.
    handleIcon: 'path://M5,0 h2 a5,5 0 0 1 5,5 v18 a5,5 0 0 1 -5,5 h-2 a5,5 0 0 1 -5,-5 v-18 a5,5 0 0 1 5,-5 z M6,9 v10',
    // Scales by the bar's height; 210% is the grip the design asks for.
    handleSize: '210%',
    handleStyle: {borderColor: '#000000', borderWidth: 1, color: CHART_PRIMARY_COLOR},
    moveHandleSize: 0,
    // No preview curve inside the bar: it repeats the plot above at a size nobody can read. No date
    // labels on the handles either — the axis above already says when.
    showDataShadow: false,
    showDetail: false,
  },
};
